import type React from 'react';

import { PaymentRequestButtonElement } from '@stripe/react-stripe-js';

import { useIsDarkMode } from '#src/app/shared/hooks/dom';
import type { WithTokenParams } from '#src/app/shared/types/payment';
import type { PaymentType } from '#src/app/shared/utils/payments';

import { useHideAriaHiddenStripeInputsFromTabOrder } from '../../hooks';

import { useOnClick } from './hooks/useOnClick';
import { usePaymentRequest } from './hooks/usePaymentRequest';
import { usePaymentRequestOnCancel } from './hooks/usePaymentRequestOnCancel';
import { usePaymentRequestOnPaymentMethod } from './hooks/usePaymentRequestOnPaymentMethod';

type Props = {
	amount: number;
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	currencyCode: string;
	label: string;
	onCancel: () => void;
	paymentType: 'applePay' | 'googlePay';
	validate?: (paymentType: PaymentType) => Promise<boolean>;
	withToken: (params: WithTokenParams) => Promise<void>;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
	elementStyleOptions?: { height?: string };
};

export const PaymentRequestButton: React.FC<Props> = ({
	// Should be in base units
	amount,

	beforeToken,
	currencyCode,
	label,
	onCancel,
	paymentType,
	validate,
	withToken,
	prePaymentChecks,
	elementStyleOptions,
}) => {
	const paymentRequest = usePaymentRequest({ amount, currencyCode, label });
	const onClick = useOnClick({ beforeToken, paymentRequest, paymentType, validate, prePaymentChecks });
	usePaymentRequestOnCancel(paymentRequest, onCancel);
	usePaymentRequestOnPaymentMethod(paymentRequest, paymentType, withToken);
	const isDarkMode = useIsDarkMode();
	const containerRef = useHideAriaHiddenStripeInputsFromTabOrder();

	if (!paymentRequest) return null;

	return (
		<div ref={containerRef} className="rounded-medium overflow-hidden">
			<PaymentRequestButtonElement
				onClick={onClick}
				options={{
					paymentRequest,
					style: {
						paymentRequestButton: {
							theme: isDarkMode ? 'light-outline' : 'dark',
							...(elementStyleOptions ? { height: elementStyleOptions.height } : {}),
						},
					},
				}}
			/>
		</div>
	);
};
